// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, TrendingUp, Dumbbell, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import StatsCard from './StatsCard';

interface StatItem {
  id: string;
  name: string;
  value: string;
  type: string | null;
  category: string;
}

interface StatsShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileId: string;
  athleteName?: string;
  athleteAvatar?: string;
  gradYear?: number | string;
  sport?: string;
  position?: string;
  onConfirm: (statsData: {
    athleteName?: string;
    athleteAvatar?: string;
    gradYear?: number | string;
    sport?: string;
    position?: string;
    stats: { name: string; value: string; category?: string }[];
  }) => void;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'combine': <Dumbbell className="h-4 w-4" />,
  'sport_specific': <TrendingUp className="h-4 w-4" />,
  'speed': <Zap className="h-4 w-4" />,
};

const formatStatName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const categorizeType = (type: string | null): string => {
  if (type === 'combine') return 'Combine Stats';
  if (type === 'sport_specific') return 'Sport Stats';
  return 'General Stats';
};

const StatsShareModal: React.FC<StatsShareModalProps> = ({
  open,
  onOpenChange,
  profileId,
  athleteName,
  athleteAvatar,
  gradYear,
  sport,
  position,
  onConfirm,
}) => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [selectedStats, setSelectedStats] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open && profileId) {
      fetchStats();
    }
  }, [open, profileId]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('profile_statistics')
        .select('id, stat_name, stat_value, type')
        .eq('profile_id', profileId)
        .not('stat_value', 'is', null);

      if (error) throw error;

      const formattedStats: StatItem[] = (data || [])
        .filter(s => s.stat_value && s.stat_value.trim() !== '')
        .map(s => ({
          id: s.id,
          name: formatStatName(s.stat_name),
          value: s.stat_value,
          type: s.type,
          category: categorizeType(s.type),
        }));

      setStats(formattedStats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStat = (statId: string) => {
    setSelectedStats(prev => {
      const next = new Set(prev);
      if (next.has(statId)) {
        next.delete(statId);
      } else {
        next.add(statId);
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    const categoryStats = stats.filter(s => s.category === category);
    const allSelected = categoryStats.every(s => selectedStats.has(s.id));
    
    setSelectedStats(prev => {
      const next = new Set(prev);
      categoryStats.forEach(s => {
        if (allSelected) {
          next.delete(s.id);
        } else {
          next.add(s.id);
        }
      });
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedStatData = stats
      .filter(s => selectedStats.has(s.id))
      .map(s => ({
        name: s.name,
        value: s.value,
        category: s.category,
      }));

    onConfirm({
      athleteName,
      athleteAvatar,
      gradYear,
      sport,
      position,
      stats: selectedStatData,
    });

    setSelectedStats(new Set());
    onOpenChange(false);
  };

  // Group stats by category
  const groupedStats = stats.reduce((acc, stat) => {
    if (!acc[stat.category]) acc[stat.category] = [];
    acc[stat.category].push(stat);
    return acc;
  }, {} as Record<string, StatItem[]>);

  // Preview data
  const previewData = {
    athleteName,
    athleteAvatar,
    gradYear,
    sport,
    position,
    stats: stats
      .filter(s => selectedStats.has(s.id))
      .map(s => ({ name: s.name, value: s.value, category: s.category })),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Share Your Stats</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : stats.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No stats found.</p>
            <p className="text-sm">Add stats to your profile first!</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col md:flex-row gap-4 overflow-hidden">
            {/* Stats Selection */}
            <div className="flex-1 min-w-0">
              <ScrollArea className="h-[300px] md:h-[400px] pr-4">
                <div className="space-y-4">
                  {Object.entries(groupedStats).map(([category, categoryStats]) => (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={categoryStats.every(s => selectedStats.has(s.id))}
                          onCheckedChange={() => toggleCategory(category)}
                        />
                        <Label
                          htmlFor={`category-${category}`}
                          className="flex items-center gap-2 font-semibold cursor-pointer"
                        >
                          {CATEGORY_ICONS[category] || <TrendingUp className="h-4 w-4" />}
                          {category}
                        </Label>
                      </div>
                      <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {categoryStats.map(stat => (
                          <div
                            key={stat.id}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent cursor-pointer"
                            onClick={() => toggleStat(stat.id)}
                          >
                            <Checkbox
                              id={stat.id}
                              checked={selectedStats.has(stat.id)}
                              onCheckedChange={() => toggleStat(stat.id)}
                            />
                            <Label htmlFor={stat.id} className="flex-1 cursor-pointer">
                              <span className="text-sm">{stat.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({stat.value})
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Preview */}
            <div className="md:w-[280px] flex-shrink-0">
              <p className="text-sm font-medium text-muted-foreground mb-2">Preview</p>
              {selectedStats.size > 0 ? (
                <div className="transform scale-90 origin-top-left">
                  <StatsCard statsData={previewData} />
                </div>
              ) : (
                <div className="border border-dashed rounded-xl p-6 text-center text-sm text-muted-foreground">
                  Select stats to see preview
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedStats.size === 0}
          >
            Add to Post ({selectedStats.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatsShareModal;
